/**
 * Shared content-card primitives for the homepage.
 *
 * `TYPE_CONFIG` (type → route + chip) and the locale-flattening helpers live
 * here so every homepage surface — the Newest row, and the personalized
 * member rails (Recently viewed / Saved / Recommended) — render an identical
 * card and route a given content type the same way.
 */
import { Link } from '@/i18n/navigation'
import { useTranslations } from 'next-intl'
import { Play, BookOpen, Scissors, FileText, GraduationCap } from 'lucide-react'
import { GatedBadge } from '@/components/content/content-gate'
import { getBlogPostPath } from '@/lib/blog-url'
import { optimizeSanityImageUrl } from '@/lib/sanity/client'
import { SquareBlurImage } from '@/components/content/square-blur-image'

// ── Route + visual mapping per content type ──────────────────────────────
// Keep the case list exhaustive so a new type added to content_index forces
// a deliberate choice here rather than silently falling through. `label` is
// the English name; the rendered chip text comes from `cards.chip.<key>`
// (every key here has a message in all 10 locales).
export const TYPE_CONFIG: Record<
  string,
  { label: string; href: (slug: string) => string; accent: string; icon: React.ReactNode }
> = {
  tutorial: {
    label: 'Tutorial',
    href: (slug) => `/tutorials/${slug}`,
    accent: 'bg-brand-600 text-white',
    icon: <Play className="h-3 w-3" />,
  },
  cas: {
    label: 'Creating a Scene',
    href: (slug) => `/creating-a-scene/${slug}`,
    accent: 'bg-purple-600 text-white',
    icon: <BookOpen className="h-3 w-3" />,
  },
  // content_index rows normalize every flogger schema to type='flogger'
  // (with a 'design' | 'knot' flogger_type discriminator — see
  // hrefForContent); the per-schema keys below serve Sanity-typed callers.
  flogger: {
    label: 'Flogger',
    href: (slug) => `/floggers/${slug}`,
    accent: 'bg-rose-600 text-white',
    icon: <Scissors className="h-3 w-3" />,
  },
  floggerDesign: {
    label: 'Flogger',
    href: (slug) => `/floggers/${slug}`,
    accent: 'bg-rose-600 text-white',
    icon: <Scissors className="h-3 w-3" />,
  },
  floggerTool: {
    label: 'Tool',
    href: (slug) => `/floggers/tools/${slug}`,
    accent: 'bg-rose-600 text-white',
    icon: <Scissors className="h-3 w-3" />,
  },
  floggerHandle: {
    label: 'Handle',
    href: (slug) => `/floggers/handles/${slug}`,
    accent: 'bg-rose-600 text-white',
    icon: <Scissors className="h-3 w-3" />,
  },
  floggerTransitionKnot: {
    label: 'Transition Knot',
    href: (slug) => `/floggers/transition-knots/${slug}`,
    accent: 'bg-rose-600 text-white',
    icon: <Scissors className="h-3 w-3" />,
  },
  floggerFallKnot: {
    label: 'Fall Knot',
    href: (slug) => `/floggers/fall-knots/${slug}`,
    accent: 'bg-rose-600 text-white',
    icon: <Scissors className="h-3 w-3" />,
  },
  post: {
    label: 'Blog',
    href: (slug) => `/blogs/${slug}`,
    accent: 'bg-amber-700 text-white',
    icon: <FileText className="h-3 w-3" />,
  },
  course: {
    label: 'Course',
    href: (slug) => `/courses/${slug}`,
    accent: 'bg-green-700 text-white',
    icon: <GraduationCap className="h-3 w-3" />,
  },
  // Lessons have no standalone page — they live in a course viewer at
  // /courses/<courseSlug>/topics/<lessonSlug>, and a content_index lesson row
  // doesn't carry its parent-course slug. So link to the /lessons/<slug>
  // resolver (src/app/[locale]/(public)/lessons/[slug]/page.tsx), which looks
  // up the parent course and redirects into the viewer (and 404s a true
  // orphan). The old /courses/lessons/<slug> target was a dead route that
  // always 404'd for any lesson not in the legacy lesson-slugs redirect map.
  lesson: {
    label: 'Lesson',
    href: (slug) => `/lessons/${slug}`,
    accent: 'bg-green-700 text-white',
    icon: <GraduationCap className="h-3 w-3" />,
  },
  // Standalone instructor-resource page. Added so inline `internalLink`
  // annotations pointing at an instructorResource resolve to their real
  // route instead of falling through to the `/blogs/` default.
  instructorResource: {
    label: 'Instructor Resource',
    href: (slug) => `/instructor-resources/${slug}`,
    accent: 'bg-slate-600 text-white',
    icon: <FileText className="h-3 w-3" />,
  },
}

/** Route for a content_index row, honoring blog + flogger subtypes. */
export function hrefForContent(
  type: string,
  slug: string,
  blogType?: string | null,
  floggerType?: string | null,
): string {
  if (type === 'post') return getBlogPostPath(slug, blogType ?? undefined)
  if (type === 'flogger')
    return floggerType === 'knot' ? `/floggers/knots/${slug}` : `/floggers/${slug}`
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.post
  return cfg.href(slug)
}

/**
 * Href for a GROQ-dereferenced content target `{ _type, slug, blog_type,
 * courseSlug }` — the shape the internalLink / glossary-explainer projections
 * resolve. lesson/quiz become a course DEEP-LINK (`/courses/<courseSlug>/topics/
 * <slug>`) using the resolved parent-course slug; everything else uses
 * hrefForContent. Returns null for a dangling ref (no type/slug) or a
 * lesson/quiz with no resolvable parent course — callers render plain text.
 */
export function hrefForContentTarget(
  target?: { _type?: string; slug?: string; blog_type?: string | null; courseSlug?: string | null } | null,
): string | null {
  if (!target?._type || !target.slug) return null
  if (target._type === 'lesson' || target._type === 'quiz') {
    return target.courseSlug ? `/courses/${target.courseSlug}/topics/${target.slug}` : null
  }
  return hrefForContent(target._type, target.slug, target.blog_type ?? undefined)
}

// Locale-aware flatteners live in a plain module so Server Components can call
// them; re-exported here for existing importers.
export { titleFromLocale, excerptFromLocale } from '@/lib/content/locale-title'

export interface ContentCardItem {
  id: string
  type: string
  slug: string
  /** Already-localized display string. */
  title: string
  excerpt?: string | null
  thumbnail_url: string | null
  is_gated?: boolean | null
  blog_type?: string | null
  /** content_index flogger discriminator ('design' | 'knot') for routing. */
  flogger_type?: string | null
}

/** The single canonical content card — cover, type chip, title, excerpt. */
export function ContentCard({
  item,
  hasAccess,
  membersBadgeLabel,
  className = '',
  footnote,
  squareImage = false,
}: {
  item: ContentCardItem
  hasAccess: boolean
  membersBadgeLabel?: string
  className?: string
  /** Optional muted caption pinned to the card bottom (e.g. "Saved Jul 12"). */
  footnote?: string | null
  /**
   * Render the cover as a full (never-cropped) square tile with a blurred
   * backdrop — the same treatment as the content libraries' default 'square'
   * card layout (SquareBlurImage). Default is the 4:3 object-cover crop used by
   * the homepage rails. Opted into by /my-content so saved covers match the
   * library grids instead of cropping off heads/ties.
   */
  squareImage?: boolean
}) {
  // One translator per card. Works in both Server (homepage rails) and Client
  // (free grid, Portable Text contentCards block) renders.
  const t = useTranslations('cards')
  const typeKey = TYPE_CONFIG[item.type] ? item.type : 'post'
  const cfg = TYPE_CONFIG[typeKey]
  const href = hrefForContent(item.type, item.slug, item.blog_type, item.flogger_type)
  return (
    <Link
      href={href}
      prefetch={false}
      className={`group relative flex flex-col overflow-hidden bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06),_0_10px_30px_rgba(0,0,0,0.05)] ring-1 ring-gray-200/70 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_40px_rgba(30,115,190,0.2),_0_1px_3px_rgba(0,0,0,0.06)] hover:ring-brand-300 dark:bg-gray-900 dark:ring-gray-800 dark:hover:ring-brand-700 ${className}`}
    >
      {squareImage && item.thumbnail_url ? (
        // Full image, never cropped — matches the libraries' 'square' layout.
        <SquareBlurImage
          src={item.thumbnail_url}
          alt=""
          imgClassName="transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className={`relative w-full overflow-hidden bg-gradient-to-br from-brand-800 via-brand-600 to-cyan-500 ${squareImage ? 'aspect-square' : 'aspect-[4/3]'}`}>
          {item.thumbnail_url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={optimizeSanityImageUrl(item.thumbnail_url, 800)}
              alt=""
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : null}
        </div>
      )}
      <div className="relative flex flex-1 flex-col p-4">
        {item.is_gated && !hasAccess && (
          <GatedBadge label={membersBadgeLabel ?? t('membersBadge')} size="sm" className="absolute right-3 top-3" />
        )}
        <span
          className={`inline-flex w-fit items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cfg.accent}`}
        >
          {cfg.icon}
          {t(`chip.${typeKey}`)}
        </span>
        <h3 className="mt-2 line-clamp-2 text-base font-semibold leading-tight text-gray-900 transition-colors group-hover:text-brand-700 dark:text-white dark:group-hover:text-brand-400">
          {item.title}
        </h3>
        {item.excerpt && (
          <p className="mt-1.5 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
            {item.excerpt}
          </p>
        )}
        {footnote && (
          <p className="mt-auto pt-3 text-xs text-gray-400 dark:text-gray-500">{footnote}</p>
        )}
      </div>
    </Link>
  )
}

/**
 * A titled, horizontally-scrolling rail of content cards. CSS-only scroll
 * (no client JS) so it stays a server component. Used for the personalized
 * member rails; renders nothing when empty.
 */
export function ContentRail({
  eyebrow,
  title,
  viewAllHref,
  viewAllLabel,
  items,
  hasAccess,
  membersBadgeLabel,
}: {
  eyebrow?: string
  title: string
  viewAllHref?: string
  viewAllLabel?: string
  items: ContentCardItem[]
  hasAccess: boolean
  membersBadgeLabel?: string
}) {
  const t = useTranslations('cards')
  if (items.length === 0) return null
  return (
    <section className="mx-auto w-full max-w-7xl px-4 py-8">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          {eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-400">
              {eyebrow}
            </p>
          )}
          <h2 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{title}</h2>
        </div>
        {viewAllHref && (
          <Link
            href={viewAllHref}
            className="shrink-0 text-sm font-medium text-brand-700 hover:underline dark:text-brand-400"
          >
            {viewAllLabel ?? t('viewAll')} →
          </Link>
        )}
      </div>
      <div className="-mx-4 flex snap-x gap-5 overflow-x-auto px-4 pb-4 [scrollbar-width:thin]">
        {items.map((item) => (
          <ContentCard
            key={item.id}
            item={item}
            hasAccess={hasAccess}
            membersBadgeLabel={membersBadgeLabel}
            className="w-[260px] shrink-0 snap-start"
            squareImage
          />
        ))}
      </div>
    </section>
  )
}
